// @ds/activity/ActivityLog — canonical activity timeline primitive.
//
// Timeline of audit/activity events: dot + connecting line left, content right.
// Entries collapse by default; click to expand before→after diff table when
// `changes` are present. Pure presentation — caller passes `entries` grouped
// by date.
//
// Rule 5b: auto-emits `data-testid="activity-entry-{id}"` per entry.
// Consumer scopes via the optional `testId` on the root container.

import { useState } from "react";
import {
  EditOutlined,
  PlusCircleOutlined,
  DeleteOutlined,
  LinkOutlined,
  DisconnectOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  CommentOutlined,
  DollarOutlined,
  EllipsisOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  ACTIVITY,
  FONT_SIZE,
  MOTION,
  SPACE,
  SURFACE,
  TEXT,
  TONE,
} from "@ds/tokens/palette";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Box } from "@ds/layout/Box";
import { Grid } from "@ds/layout/Grid";
import { TextValue } from "@ds/display/TextValue";
import { Eyebrow } from "@ds/display/Eyebrow";
import { Hairline } from "@ds/display/Hairline";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ActivityKind =
  | "created"
  | "edited"
  | "deleted"
  | "linked"
  | "unlinked"
  | "commented"
  | "paid"
  | "synced"
  | "approved"
  | "rejected"
  | "custom";

export interface ActivityActor {
  name: string;
  avatar?: string;
}

export interface ActivityChange {
  field: string;
  before?: string | null;
  after?: string | null;
}

export interface ActivityEntry {
  id: number | string;
  /** ISO timestamp or pre-formatted display string (e.g. "07.01 · 09:14"). */
  at: string;
  kind: ActivityKind;
  actor: ActivityActor;
  /** Human-readable verb / description, e.g. "Zaktualizowano". */
  label: string;
  /** Optional before→after field diff. Presence enables expand/collapse. */
  changes?: ActivityChange[];
}

export interface ActivityLogGroup {
  /** Group header label, e.g. "Dzisiaj", "07 sty 2026". */
  date: string;
  entries: ActivityEntry[];
}

// ─── Inputs / Outputs / Actions ───────────────────────────────────────────────

/** Data props — the model. */
export interface ActivityLogInput {
  /** Entries grouped by date. Caller is responsible for grouping and sorting. */
  groups: ActivityLogGroup[];
  /** Fallback text rendered when all groups are empty. */
  emptyText?: string;
  /**
   * Applied as `data-testid` on the root container. Inner testids
   * (`activity-entry-{id}`) are always auto-emitted regardless of this prop.
   */
  testId?: string;
}

/** Event callbacks. No events — purely presentational. */
export type ActivityLogOutput = object;

/** Configurable affordances. No side actions. */
export type ActivityLogActions = object;

export type ActivityLogProps = ActivityLogInput & ActivityLogOutput;

// ─── Kind → dot config ────────────────────────────────────────────────────────

type DotConfig = {
  bg: string;
  border: string;
  iconColor: string;
  icon: React.ReactNode;
};

const KIND_DOT: Record<ActivityKind, DotConfig> = {
  created:   { bg: TONE.green.softBg,  border: TONE.green.border,  iconColor: TONE.green.base,    icon: <PlusCircleOutlined /> },
  edited:    { bg: TONE.blue.softBg,   border: TONE.blue.border,   iconColor: TONE.blue.base,     icon: <EditOutlined /> },
  deleted:   { bg: TONE.red.softBg,    border: TONE.red.border,    iconColor: TONE.red.base,      icon: <DeleteOutlined /> },
  linked:    { bg: TONE.teal.softBg,   border: TONE.teal.border,   iconColor: TONE.teal.base,     icon: <LinkOutlined /> },
  unlinked:  { bg: TONE.amber.softBg,  border: TONE.amber.border,  iconColor: TONE.amber.base,    icon: <DisconnectOutlined /> },
  commented: { bg: TONE.purple.softBg, border: TONE.purple.border, iconColor: TONE.purple.base,   icon: <CommentOutlined /> },
  paid:      { bg: TONE.green.softBg,  border: TONE.green.border,  iconColor: TONE.green.base,    icon: <DollarOutlined /> },
  synced:    { bg: TONE.blue.softBg,   border: TONE.blue.border,   iconColor: TONE.blue.base,     icon: <SyncOutlined /> },
  approved:  { bg: TONE.green.softBg,  border: TONE.green.border,  iconColor: TONE.green.text,    icon: <CheckCircleOutlined /> },
  rejected:  { bg: TONE.red.softBg,    border: TONE.red.border,    iconColor: TONE.red.text,      icon: <CloseCircleOutlined /> },
  custom:    { bg: TONE.grey.soft,     border: TONE.grey.border,   iconColor: TEXT.muted,         icon: <EllipsisOutlined /> },
};

// ─── Internal components ──────────────────────────────────────────────────────

function KindDot({ kind }: { kind: ActivityKind }) {
  const cfg = KIND_DOT[kind] ?? KIND_DOT.custom;
  // Bespoke leaf: fixed-size circle with absolute-positioned z-index above
  // the connecting line. No Box variant matches.
  return (
    <span
      style={{
        display:        "inline-flex",
        alignItems:     "center",
        justifyContent: "center",
        width:          ACTIVITY.dotSize,
        height:         ACTIVITY.dotSize,
        borderRadius:   "50%",
        background:     cfg.bg,
        border:         `${ACTIVITY.dotBorderWidth}px solid ${cfg.border}`,
        fontSize:       FONT_SIZE.small,
        color:          cfg.iconColor,
        flexShrink:     0,
        zIndex:         1,
        position:       "relative",
      }}
    >
      {cfg.icon}
    </span>
  );
}

function ChangeTable({ changes }: { changes: ActivityChange[] }) {
  // Bordered card wrapper + row dividers via Stack `divider="soft"`. The
  // prior bespoke field-column background tint is dropped — Box `tone="muted"`
  // on the field cell carries the same intent within the closed-enum API.
  return (
    <Box border shape="rounded-sm" paddingY="none">
      <Stack gap="none" divider="soft">
        {changes.map((ch, i) => (
          <Grid key={i} columns="label-value" gap="none">
            <Box tone="muted" paddingX="md" paddingY="xs">
              <TextValue as="div" size="xs" tone="secondary" weight="medium">
                {ch.field}
              </TextValue>
            </Box>
            <Box paddingX="md" paddingY="xs">
              <Inline gap="sm" wrap align="center">
                {ch.before != null && ch.before !== "" ? (
                  <TextValue as="span" tone="muted" strikethrough>
                    {ch.before}
                  </TextValue>
                ) : (
                  ch.after != null &&
                  ch.before === null && (
                    <TextValue as="span" tone="faint" italic>—</TextValue>
                  )
                )}
                {ch.before != null && ch.after != null && (
                  <TextValue as="span" tone="faint" size="xs">→</TextValue>
                )}
                {ch.after != null && ch.after !== "" ? (
                  <TextValue as="span" tone="primary" weight="medium">
                    {ch.after}
                  </TextValue>
                ) : (
                  ch.before != null &&
                  ch.after === null && (
                    <TextValue as="span" tone="faint" italic>usunięto</TextValue>
                  )
                )}
              </Inline>
            </Box>
          </Grid>
        ))}
      </Stack>
    </Box>
  );
}

function Entry({ entry, isLast }: { entry: ActivityEntry; isLast: boolean }) {
  const [open, setOpen] = useState(false);
  const hasChanges  = (entry.changes?.length ?? 0) > 0;
  const changeCount = entry.changes?.length ?? 0;

  return (
    <Inline
      testId={`activity-entry-${entry.id}`}
      gap="sm"
      align="start"
    >
      {/* Left: dot + connecting line */}
      <Box shrink position="relative">
        <Stack gap="xs" align="center">
          <KindDot kind={entry.kind} />
          {!isLast && (
            // 1px-wide connecting line — bespoke leaf chrome (no Box width prop);
            // documented in /tmp/needed-primitives.md.
            <div
              style={{
                width:      1,
                flex:       1,
                minHeight:  SPACE.lg,
                background: SURFACE.border,
              }}
            />
          )}
        </Stack>
      </Box>

      {/* Right: content */}
      <Box grow paddingY={isLast ? "none" : "lg"}>
        {/* One-liner row */}
        <Box
          paddingY="xs"
          onClick={hasChanges ? () => setOpen((v) => !v) : undefined}
        >
          <Inline gap="sm">
            <TextValue as="span" size="sm" tone="primary">
              {entry.label}
            </TextValue>

            {hasChanges && (
              <TextValue as="span" size="sm" tone="muted">
                ·{" "}
                {changeCount === 1
                  ? "1 pole zmienione"
                  : changeCount < 5
                    ? `${changeCount} pola zmienione`
                    : `${changeCount} pól zmienionych`}
              </TextValue>
            )}

            {hasChanges && (
              <RightOutlined
                style={{
                  fontSize:   FONT_SIZE.micro,
                  color:      TEXT.faint,
                  transform:  open ? "rotate(90deg)" : "rotate(0deg)",
                  transition: `transform ${MOTION.fast} ${MOTION.ease}`,
                  lineHeight: 1,
                }}
              />
            )}

            <Box as="span" grow />

            <TextValue as="span" size="sm" tone="faint" nowrap>
              {entry.actor.name}
              {" "}
              <TextValue as="span" tone="grey">·</TextValue>
              {" "}
              {entry.at}
            </TextValue>
          </Inline>
        </Box>

        {/* Expanded diff */}
        {open && hasChanges && entry.changes && (
          <ChangeTable changes={entry.changes} />
        )}
      </Box>
    </Inline>
  );
}

function DateGroupHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        padding:     `${SPACE.sm}px 0 ${SPACE.sm}px ${ACTIVITY.dotSize + ACTIVITY.entryGap}px`,
        marginTop:   SPACE.xs,
      }}
    >
      <Eyebrow>{label}</Eyebrow>
    </div>
  );
}

// ─── ActivityLog ──────────────────────────────────────────────────────────────

export function ActivityLog({
  groups,
  emptyText = "Brak aktywności",
  testId,
}: ActivityLogProps) {
  const allEmpty = groups.every((g) => g.entries.length === 0);

  if (allEmpty) {
    return (
      <Box testId={testId} paddingY="xl">
        <TextValue as="div" tone="faint" size="sm" align="center">
          {emptyText}
        </TextValue>
      </Box>
    );
  }

  return (
    <div data-testid={testId}>
      {groups.map((group, gi) => {
        if (group.entries.length === 0) return null;
        return (
          <div key={gi}>
            <DateGroupHeader label={group.date} />
            {group.entries.map((entry, ei) => {
              const isLastInGroup = ei === group.entries.length - 1;
              const isLastGroup   = gi === groups.length - 1;
              return (
                <Entry
                  key={entry.id}
                  entry={entry}
                  isLast={isLastInGroup && isLastGroup}
                />
              );
            })}
            {gi < groups.length - 1 && (
              <Hairline variant="soft" spacing={SPACE.sm} />
            )}
          </div>
        );
      })}
    </div>
  );
}
